import { Component, computed, input, LOCALE_ID, OnInit, output, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ThemeService } from "@tylertech/core-module";
import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { ICategoryChartData, IMetadataChart, IPieChartData, ChartType as MetadataChartType } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartConfigurationCustomTypesPerDataset, Chart } from "chart.js";
import { ValueChangedParms } from "../../interfaces";
import { MetadataEventService } from "../../services/metadataEventService";
import { MetadataChartConverter } from "./chartConverter";

/** simple module-scoped counter to generate instance-unique id for MetadataChartComponent */
let counter = 0;

@Component({
    selector: "metadata-chart",
    standalone: false,
    templateUrl: "./metadataChart.tmpl.html",
    styles: `chartjs-container {
    min-height: 300px;
}`,
})
export class MetadataChartComponent implements OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $themeService = inject(ThemeService);

    private readonly log: ILogger;

    readonly model = input<Readonly<IMetadataChart>>();

    readonly valueChanged = output<ValueChangedParms>();

    protected readonly listenerId = `MetadataChartComponent[${counter++}]`;

    readonly hasData = computed(() =>
    {
        const model = this.model();
        return !model
            || (model.Type === MetadataChartType.Pie
                ? !!(model as IPieChartData).Data?.length
                : !!(model as ICategoryChartData).Series?.length);
    });

    chartJsConfig: ChartConfiguration<"pie"> | ChartConfiguration<"bar"> | ChartConfiguration<"line"> | ChartConfigurationCustomTypesPerDataset;

    constructor()
    {
        this.log = getLoggerFor("MetadataChartComponent",
            () => this.model().Title
                ? `MetadataChartComponent("${this.model().Title}")`
                : `MetadataChartComponent("")`);

        this.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (args.Recalculating)
                {
                    return;
                }

                this.setChartData();
            });

        Chart.defaults.color = this.$themeService.chartStyleColors().color;
        Chart.defaults.borderColor = this.$themeService.chartStyleColors().borderColor;
    }

    ngOnInit(): void
    {
        this.setChartData();
    }

    @LogInvocation(LogLevel.DEBUG)
    protected setChartData(): void
    {
        if (!this.hasData())
        {
            return;
        }

        this.chartJsConfig = new MetadataChartConverter(
            this.model(), this.locale, this.$themeService.colorSeries(), this.$themeService.chartStyleColors())
            .convertChart();
    }
}

